<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">Edit Asset</oxd-text>
      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="asset.serialNumber"
              label="Serial Number"
              :rules="rules.serialNumber"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <asset-brand-autocomplete
              :key="asset.assetBrand"
              v-model="asset.assetBrand"
              label="Asset Brand"
              :rules="rules.assetBrand"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="asset.model"
              label="Model"
              :rules="rules.model"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <asset-category-autocomplete
              :key="asset.assetCategory"
              v-model="asset.assetCategory"
              label="Asset Category"
              :rules="rules.assetCategory"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <asset-vendor-autocomplete
              :key="asset.assetVendor"
              v-model="asset.assetVendor"
              label="Asset Vendor"
              :rules="rules.assetVendor"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="asset.description"
              label="Description"
              type="textarea"
              :rules="rules.description"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="asset.acquired"
              label="Acquired Date"
              :rules="rules.acquired"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="asset.warrantyStart"
              label="Warranty Start Date"
              :rules="rules.warrantyStart"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="asset.warrantyEnd"
              label="Warranty End Date"
              :rules="rules.warrantyEnd"
              :years="yearsArray"
            />
          </oxd-grid-item>
        </oxd-grid>
        <oxd-text class="orangehrm-sub-title" tag="h6">
          Assignment Information
        </oxd-text>
        <oxd-divider />
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <employee-autocomplete
              v-model="asset.empNumber"
              :rules="rules.employee"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="asset.assignedFrom"
              label="Assigned From"
              :rules="rules.assignedFrom"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <file-upload-input
              v-model:newFile="asset.newPicture"
              v-model:method="asset.method"
              :file="asset.oldPicture"
              type="file"
              label="Picture"
              :button-label="$t('general.browse')"
              :rules="rules.picture"
              :url="`admin/viewAsset/assetId`"
            />
            <oxd-text class="orangehrm-input-hint" tag="p">
              {{ $t('general.accepts_up_to_1mb') }}
            </oxd-text>
          </oxd-grid-item>
        </oxd-grid>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            type="button"
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  required,
  shouldNotExceedCharLength,
  validFileTypes,
  maxFileSize,
} from '@ohrm/core/util/validation/rules';
import AssetCategoryAutocomplete from '@/orangehrmAdminPlugin/components/AssetCategoryAutocomplete.vue';
import AssetBrandAutocomplete from '@/orangehrmAdminPlugin/components/AssetBrandAutocomplete.vue';
import AssetVendorAutocomplete from '@/orangehrmAdminPlugin/components/AssetVendorAutocomplete.vue';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import FileUploadInput from '@/core/components/inputs/FileUploadInput';
import {yearRange} from '@ohrm/core/util/helper/year-range';

export default {
  name: 'EditAsset',
  components: {
    'asset-category-autocomplete': AssetCategoryAutocomplete,
    'asset-brand-autocomplete': AssetBrandAutocomplete,
    'asset-vendor-autocomplete': AssetVendorAutocomplete,
    'employee-autocomplete': EmployeeAutocomplete,
    'file-upload-input': FileUploadInput,
  },
  props: {
    assetId: {
      type: Number,
      required: true,
    },
    allowedFileTypes: {
      type: Array,
      required: true,
    },
    maxFileSize: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/assets',
    );
    return {
      http,
    };
  },

  data() {
    return {
      isLoading: false,
      yearsArray: [...yearRange()],
      asset: {
        id: '',
        serialNumber: '',
        assetBrand: {id: null, label: null},
        assetVendor: {id: null, label: null},
        assetCategory: {id: null, label: null},
        model: '',
        description: '',
        acquired: '',
        warrantyStart: '',
        warrantyEnd: '',
        empNumber: {empNumber: null, label: null},
        assignedFrom: '',
        oldPicture: '',
        newPicture: null,
        method: 'keepCurrent',
      },
      rules: {
        serialNumber: [required, shouldNotExceedCharLength(100)],
        picture: [
          (v) => {
            if (this.asset.method == 'replaceCurrent') {
              return required(v);
            } else {
              return true;
            }
          },
          validFileTypes(this.allowedFileTypes),
          maxFileSize(this.maxFileSize),
        ],
      },
    };
  },

  created() {
    this.isLoading = true;
    this.http
      .get(this.assetId)
      .then((response) => {
        const {data} = response.data;
        this.asset.id = data.id;
        this.asset.serialNumber = data.serialNumber;
        this.asset.assetBrand = {
          id: data.assetBrand.id,
          label: data.assetBrand.name,
        };
        this.asset.assetCategory = {
          id: data.assetCategory.id,
          label: data.assetCategory.name,
        };
        this.asset.assetVendor = {
          id: data.assetVendor.id,
          label: data.assetVendor.name,
        };
        this.asset.empNumber = {
          id: data.employee.empNumber,
          label: `${data.employee.firstName} ${data.employee.middleName} ${data.employee.lastName}`,
          isPastEmployee: data.employee.terminationId,
        };
        this.asset.model = data.model;
        this.asset.description = data.description;
        this.asset.acquired = data.acquired;
        this.asset.warrantyStart = data.warrantyStart;
        this.asset.warrantyEnd = data.warrantyEnd;
        this.asset.assignedFrom = data.assignedFrom;
        this.asset.oldPicture = data.picture?.filename ? data.picture : null;
        this.asset.newPicture = null;
        this.asset.method = 'keepCurrent';
        // Fetch list data for unique test
        return this.http.getAll({limit: 0});
      })
      .then((response) => {
        const {data} = response.data;
        this.rules.serialNumber.push((v) => {
          const index = data.findIndex(
            (item) =>
              String(item.serialNumber).toLowerCase() ==
              String(v).toLowerCase(),
          );
          if (index > -1) {
            const {id} = data[index];
            return id !== this.asset.id
              ? this.$t('general.already_exists')
              : true;
          } else {
            return true;
          }
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onSave() {
      this.isLoading = true;
      this.http
        .update(this.assetId, {
          serialNumber: this.asset.serialNumber,
          assetBrand: this.asset.assetBrand?.id,
          assetVendor: this.asset.assetVendor?.id,
          assetCategory: this.asset.assetCategory?.id,
          model: this.asset.model,
          description: this.asset.description,
          acquired: this.asset.acquired,
          warrantyStart: this.asset.warrantyStart,
          warrantyEnd: this.asset.warrantyEnd,
          empNumber: this.asset.empNumber?.id,
          assignedFrom: this.asset.assignedFrom,
          picture: this.asset.newPicture,
          currentPicture: this.asset.method,
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.onCancel();
        });
    },
    onCancel() {
      navigate('/admin/viewAsset');
    },
  },
};
</script>
